import { FC, useContext, useEffect, useRef, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { LoadingOverlay } from "cayo.ui";
import { AppContext } from "@cayo/shared/components/App/appContext";
import { appSignals } from "@cayo/shared/components/App/utils";
import { showMessage } from "@cayo/shared/components/App/utils/showMessage";
import logger from "@cayo/shared/libs/logger";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import { appSettings } from "@cayo/shared/settings/appSettings";
import globals from "@cayo/shared/settings/globals";
import tokenStorage from "@cayo/shared/settings/tokenStorage";
import { queryUtils } from "@cayo/shared/utils/query";
import { urlUtils } from "@cayo/shared/utils/url";
import useAuth from "./hooks";
import redirectUtils from "./redirect-utils";
import { getProductState, getProductSummary } from "../../api/app-api";
import { settingsEndpoints } from "../../api/settings-endpoints";
import { createSSO, getSSO, isValidAuthMode } from "../../api/sso";
import { ProductContext, ProductStateType } from "../../contexts/product-context";
import NewAlertsPopup from "../Alerts/NewAlertsPopup";
import ODataApp from "../App/ODataApp";
import { ContextPanelHolder } from "../AppRoots";
import ActionDialogHolder from "../AppRoots/ActionDialogHolder";
import { FormsHolder } from "../AppRoots/FormsHolder";
import PropertiesPanelHolder from "../AppRoots/PropertiesPanelHolder/PropertiesPanelHolder";
import { WizardStuff } from "../AppRoots/WizardStuff";
import { initFRPage } from "../Freemium/utils";
import { getSubscriptionExpiredViewModel, HostedLicenseExpired } from "../HostedLicenseExpired";
import SchemeLoader from "../Schemes/SchemeLoader";

const log = logger.getLogger("Pages");

const observer = new MutationObserver(() => {
  if (document.querySelector(".freemeum-fr-page")) {
    initFRPage();
    observer.disconnect();
  }
});

observer.observe(document.body, { childList: true, subtree: true });

const Pages: FC = () => {
  const location = useLocation();
  const accessToken = tokenStorage.accessToken;
  const userSetting = appSettings.userSetting.get();
  const loginHint = userSetting?.loginHint;
  const isHttp = !globals.isSecureConnection;
  const isSSOInProgress = useRef<boolean>();
  const [productState, setProductState] = useState<ProductStateType | null>(null);
  const [ssoReady, setSsoReady] = useState(false);
  const { manifest } = useContext(AppContext);

  const { auth_server, oauth_client } = manifest?.client_config || {};

  const isIFrame = window.self !== window.top;
  const { isLoggedIn } = useAuth({
    tokenEndpoint: auth_server?.tokenEndpointUri || endpoints.tokenEndpoint,
    tokenStorage,
    oauthClient: oauth_client,
    authorityUri: auth_server?.authorityUri,
  });

  const expiredVm = productState?.isHosted ? getSubscriptionExpiredViewModel(productState) : null;

  useEffect(() => {
    if (manifest && !isHttp) {
      const sso = createSSO(oauth_client, auth_server);

      sso.ready.catch(() => null).finally(() => setSsoReady(true));
    }
  }, [manifest]);

  useEffect(() => {
    appSignals.subscribe("handleAuth", (data) => {
      ssoAuthHandler(isHttp ? "bearer" : data?.authMode || "");
    });
  }, [isHttp]);

  useEffect(() => {
    async function initProductState() {
      try {
        const [summaryRes, subscriptionRes] = await Promise.all([
          getProductSummary(settingsEndpoints.productConfigSummary),
          getProductState(),
        ]);

        setProductState({
          subscriptionType: subscriptionRes.subscriptionType,
          subscriptions: subscriptionRes.subscriptions,
          isHosted: summaryRes.isHosted,
          useLegacyLicensing: summaryRes.useLegacyLicensing,
        });
      } catch (error) {
        log.debug("get product state error:", error);
      }
    }

    if (isLoggedIn && manifest?.short_name === "cayo_guardian") {
      initProductState();
    }
  }, [isLoggedIn, manifest]);

  useEffect(() => {
    log.debug("isLoggedIn changed", isLoggedIn);

    if (isLoggedIn) {
      // hide errors from previous login session
      showMessage("");
    }
  }, [isLoggedIn]);

  const ssoAuthHandler = (authMode: string) => {
    if (isValidAuthMode(authMode) && !isSSOInProgress.current) {
      if (!tokenStorage.accessToken && !isHttp) {
        appSignals.send("signingIn", { signingIn: true });
        log.debug("search: " + location.pathname, "isIFrame", isIFrame);

        isSSOInProgress.current = true;
        const isSrvEndpoint = endpoints.apiEndpoint.toString();

        if (!isIFrame) {
          localStorage.setItem(
            redirectUtils.redirectParameterName,
            urlUtils.trim(window.location.hash)
          );
        }

        log.debug("sso redirect, isSrvEndpoint: " + isSrvEndpoint);
        log.debug("sso redirect, hash: " + window.location.hash);

        getSSO()
          .acquireTokenOrRedirect({
            loginHint,
            redirectUri: isSrvEndpoint,
            currentHash: window.location.hash,
            isIFrame,
            authorityUri: auth_server?.authorityUri,
          })
          .then((result) => {
            if (result) {
              if (result.type === "download") {
                window.location.assign("/download");
              } else if (result.type === "token") {
                window.location.assign(result.redirectUrl);
              }
            }
          })
          .finally(() => {
            isSSOInProgress.current = false;
            appSignals.send("signingIn", { signingIn: false });
            log.debug("isSSOInProgress", isSSOInProgress.current);
          });
      }
    }
  };

  if (isIFrame || isSSOInProgress.current) {
    log.debug("isSSOInProgress", isSSOInProgress.current, "Signing in...");
    return <LoadingOverlay text={"Signing in..."} />;
  }

  if (manifest && !isHttp && !ssoReady) {
    return <LoadingOverlay text={"Initializing..."} />;
  }

  if (!accessToken && location.pathname.toLowerCase() !== "/login") {
    const loginUrl = redirectUtils.getLoginUrl();

    return <Redirect to={loginUrl} />;
  }

  if (accessToken) {
    const file = queryUtils.getQueryParam("file");
    if (file) {
      return downloadLandingPage(file);
    } else if (window.location.hash?.endsWith("$value")) {
      let filePath = queryUtils.getQueryParam(redirectUtils.redirectParameterName);
      if (!filePath) {
        filePath = urlUtils.trim(window.location.hash);
      }

      const fileUrl = endpoints.publicUrl + "/v0/" + filePath;
      return downloadLandingPage(fileUrl);
    }
  }

  if (accessToken && location.pathname.toLowerCase() === "/login") {
    log.debug("login success -> currentLocation", location);

    const urlAfterLogin = locationAfterLogin(location);
    log.debug("urlAfterLogin: ", urlAfterLogin);

    return <Redirect to={urlAfterLogin} />;
  }

  let schemeId = location.pathname.startsWith("/")
    ? location.pathname.substr(1)
    : location.pathname;

  if (schemeId !== "login") {
    schemeId = "";
  }

  if (schemeId === "login") {
    return <SchemeLoader schemePath="login" />;
  }

  if (expiredVm) {
    return <HostedLicenseExpired {...expiredVm} />;
  }

  return (
    <ProductContext.Provider value={{ ...productState }}>
      <ODataApp isLoggedIn={isLoggedIn}>
        <NewAlertsPopup />
        <PropertiesPanelHolder />
        <ActionDialogHolder />
        <ContextPanelHolder />

        <SchemeLoader schemePath="home" />

        <WizardStuff isLoggedIn={isLoggedIn} />
        <FormsHolder />
      </ODataApp>
    </ProductContext.Provider>
  );
};

const downloadLandingPage = (url: string) => {
  log.debug("downloadLandingPage", url);
  localStorage.setItem("download", url);
  return <Redirect to={"/download"} />;
};

const locationAfterLogin = (location: { pathname: string; search: string }) => {
  log.debug("locationAfterLogin -> location", location);
  const search = new URLSearchParams(location.search);
  let redirectUrl = search.get("redirectUrl");
  const action = search.get("action");

  if (!redirectUrl || redirectUrl.toLowerCase() === "login") {
    return "/";
  }

  if (!redirectUrl.startsWith("/")) {
    redirectUrl = "/" + redirectUrl;
  }

  if (action) {
    redirectUrl += (redirectUrl.includes("?") ? "&" : "?") + "action=" + action;
  }

  return redirectUrl;
};

export default Pages;
